/**
 * Forge Modal Components — Demo + New Component modals
 * Extracted from ParagonLibraryPanel.tsx (TASK-009)
 */
import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { X } from 'lucide-react'
import type { ForgeTemplate } from './forge-types'
import { KNOWN_CATEGORIES } from './forge-types'
import { getDemoContent } from './forge-demos'

/* ── Demo Modal: renders the full interactive component ── */
export function ComponentDemoModal({
  template,
  onClose,
}: {
  template: ForgeTemplate
  onClose: () => void
}) {
  const cat = template.category.toLowerCase()
  const config = template.config as Record<string, string>

  const demoContent = getDemoContent(cat, config)

  return (
    <div
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose()
      }}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Escape' || e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onClose()
        }
      }}
      style={{
        position: 'fixed', inset: 0, zIndex: 100,
        background: 'rgba(0,0,0,0.75)',
        backdropFilter: 'blur(8px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 24,
        animation: 'fadeIn 0.2s ease-out',
      }}
    >

      <div
        style={{
          background: '#0f0f14',
          border: '1px solid #27272a',
          borderRadius: 16,
          padding: 32,
          maxWidth: '90vw',
          maxHeight: '85vh',
          overflowY: 'auto',
          animation: 'scaleIn 0.25s ease-out',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 24,
        }}
      >
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
          <div>
            <h2 style={{ fontSize: 20, fontWeight: 700, color: '#fafafa', marginBottom: 4 }}>
              {template.name}
            </h2>
            <p style={{ fontSize: 12, color: '#71717a' }}>Interactive demo — try hovering, clicking, and flipping</p>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'rgba(255,255,255,0.06)',
              border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: 8,
              padding: '6px 14px',
              fontSize: 13,
              color: '#a1a1aa',
              cursor: 'pointer',
              transition: 'background 0.2s, color 0.2s',
              fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.1)' }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.06)' }}
          >
            ✕ Close
          </button>
        </div>

        {/* Live demo area */}
        <div style={{
          padding: 32,
          background: '#09090b',
          borderRadius: 12,
          border: '1px solid #1e1e24',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 300,
        }}>
          {demoContent}
        </div>

        {/* Hint */}
        <p style={{ fontSize: 11, color: '#52525b', textAlign: 'center' }}>
          This is a live component preview. Use Insert → to add it to a tenant&apos;s editor.
        </p>
      </div>
    </div>
  )
}




export function NewComponentModal({
  onClose,
  onCreated,
}: {
  onClose: () => void
  onCreated: () => void
}) {
  const queryClient = useQueryClient()
  const [form, setForm] = useState({
    name: '',
    category: 'stylist',
    thumbnail_url: '',
    config: '{}',
  })
  const [configError, setConfigError] = useState('')

  const mutation = useMutation({
    mutationFn: async () => {
      let parsedConfig: Record<string, unknown> = {}
      try { parsedConfig = JSON.parse(form.config) } catch {
        throw new Error('Invalid JSON in config')
      }
      const { data: { user }, error: authError } = await supabase.auth.getUser()
      if (authError || !user) throw new Error('Not authenticated')
      const { error } = await supabase.from('paragon_forge_templates').insert({
        name:          form.name.trim(),
        category:      form.category,
        thumbnail_url: form.thumbnail_url.trim() || null,
        ast_payload:   parsedConfig as import('@/types/database.types').Json,
        created_by:    user.id,
      })
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['paragon-forge-templates'] })
      onCreated()
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!form.name.trim()) return
    setConfigError('')
    try { JSON.parse(form.config) } catch {
      setConfigError('JSON is invalid — fix before saving')
      return
    }
    mutation.mutate()
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" onClick={(e) => { if (e.target === e.currentTarget) onClose() }} role="presentation">
      <div className="absolute inset-0 bg-black/40 dark:bg-black/60 backdrop-blur-sm" />
      <div className="relative w-[480px] rounded-2xl bg-white dark:bg-[#171717] border border-black/[0.09] dark:border-white/[0.08] shadow-2xl flex flex-col overflow-hidden" role="dialog" aria-modal="true">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-black/[0.07] dark:border-white/[0.06]">
          <div>
            <div className="text-[15px] text-zinc-900 dark:text-zinc-50 font-medium">New Component</div>
            <div className="text-[11px] text-zinc-400 dark:text-white/25 mt-0.5">Add a reusable template to the Forge</div>
          </div>
          <button onClick={onClose} className="w-7 h-7 rounded-lg flex items-center justify-center text-zinc-400 dark:text-white/25 hover:bg-black/[0.05] dark:hover:bg-white/[0.05] hover:text-zinc-600 dark:hover:text-white/50 transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex flex-col gap-4 p-6 overflow-y-auto">
          {/* Name */}
          <div>
            <label className="block text-[11px] text-zinc-500 dark:text-white/30 tracking-wider font-mono mb-1.5">NAME <span className="text-[rgba(248,113,113,0.7)]">*</span></label>
            <input
              type="text"
              value={form.name}
              onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              placeholder="e.g. Staff Spotlight Card"
              required
              className="w-full px-3 py-2.5 rounded-lg text-[13px] text-zinc-900 dark:text-zinc-50 bg-zinc-50 dark:bg-white/[0.04] border border-black/[0.08] dark:border-white/[0.07] placeholder-zinc-400/70 dark:placeholder-white/15 focus:border-[rgba(201,168,76,0.4)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[rgba(201,168,76,0.3)] transition-colors"
            />
          </div>

          {/* Category */}
          <div>
            <label className="block text-[11px] text-zinc-500 dark:text-white/30 tracking-wider font-mono mb-1.5">CATEGORY</label>
            <select
              value={form.category}
              onChange={e => setForm(f => ({ ...f, category: e.target.value }))}
              className="w-full px-3 py-2.5 rounded-lg text-[13px] text-zinc-900 dark:text-zinc-50 bg-zinc-50 dark:bg-white/[0.04] border border-black/[0.08] dark:border-white/[0.07] focus:border-[rgba(201,168,76,0.4)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[rgba(201,168,76,0.3)] transition-colors cursor-pointer appearance-none"
            >
              {KNOWN_CATEGORIES.map(cat => (
                <option key={cat} value={cat} className="bg-white dark:bg-[#171717]">
                  {cat.charAt(0).toUpperCase() + cat.slice(1)}
                </option>
              ))}
            </select>
          </div>

          {/* Thumbnail URL */}
          <div>
            <label className="block text-[11px] text-zinc-500 dark:text-white/30 tracking-wider font-mono mb-1.5">THUMBNAIL URL <span className="text-zinc-400 dark:text-white/20 normal-case font-sans">(optional)</span></label>
            <input
              type="url"
              value={form.thumbnail_url}
              onChange={e => setForm(f => ({ ...f, thumbnail_url: e.target.value }))}
              placeholder="https://..."
              className="w-full px-3 py-2.5 rounded-lg text-[13px] text-zinc-900 dark:text-zinc-50 bg-zinc-50 dark:bg-white/[0.04] border border-black/[0.08] dark:border-white/[0.07] placeholder-zinc-400/70 dark:placeholder-white/15 focus:border-[rgba(201,168,76,0.4)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[rgba(201,168,76,0.3)] transition-colors"
            />
          </div>

          {/* Config JSON */}
          <div>
            <label className="block text-[11px] text-zinc-500 dark:text-white/30 tracking-wider font-mono mb-1.5">
              CONFIG JSON <span className="text-zinc-400 dark:text-white/20 normal-case font-sans">(default props)</span>
            </label>
            <textarea
              value={form.config}
              onChange={e => { setForm(f => ({ ...f, config: e.target.value })); setConfigError('') }}
              rows={5}
              placeholder="{}"
              className="w-full px-3 py-2.5 rounded-lg text-[11px] font-mono text-zinc-700 dark:text-white/70 bg-zinc-100 dark:bg-black/40 border border-black/[0.08] dark:border-white/[0.07] focus:border-[rgba(201,168,76,0.4)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[rgba(201,168,76,0.3)] resize-none transition-colors leading-relaxed"
            />
            {configError && <div className="text-[11px] text-[rgba(248,113,113,0.8)] mt-1">{configError}</div>}
          </div>

          {mutation.error && (
            <div className="text-[12px] text-[rgba(248,113,113,0.8)] bg-[rgba(248,113,113,0.06)] border border-[rgba(248,113,113,0.12)] rounded-lg px-3 py-2">
              {(mutation.error as Error).message}
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center gap-2 pt-1">
            <button type="button" onClick={onClose} className="flex-1 py-2.5 rounded-lg text-[13px] text-zinc-500 dark:text-white/35 border border-black/[0.07] dark:border-white/[0.06] hover:bg-zinc-50 dark:hover:bg-white/[0.03] transition-colors">
              Cancel
            </button>
            <button
              type="submit"
              disabled={!form.name.trim() || mutation.isPending}
              className="flex-1 py-2.5 rounded-lg text-[13px] text-black font-medium bg-[#C9A84C] hover:bg-[#D4B85C] transition-[background-color,color,border-color,opacity] disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {mutation.isPending ? 'Saving…' : 'Add to Forge'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
